import { useEffect, useState } from 'react';

const APPLE_SCRIPT_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID?.trim() || '';
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI?.trim() || '';

export const appleOAuthEnabled = Boolean(APPLE_CLIENT_ID && APPLE_REDIRECT_URI);

export function buildAppleAuthorizationConfig(clientId, redirectURI, state, nonce) {
	return {
		clientId,
		scope: 'name email',
		redirectURI,
		state,
		nonce,
		usePopup: true,
	};
}

export function buildAppleLoginPayload(authorization, redirectURI, nonce) {
	return {
		code: authorization?.code,
		...(authorization?.id_token ? { identity_token: authorization.id_token } : {}),
		redirect_uri: redirectURI,
		nonce,
		state: authorization?.state,
	};
}

function randomValue() {
	if (window.crypto?.randomUUID) return window.crypto.randomUUID();
	if (window.crypto?.getRandomValues) {
		const bytes = new Uint8Array(16);
		window.crypto.getRandomValues(bytes);
		return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
	}
	throw new Error('Secure randomness is unavailable for Apple sign-in.');
}

function loadAppleScript() {
	if (window.AppleID?.auth) return Promise.resolve();
	const existing = document.querySelector(`script[src="${APPLE_SCRIPT_SRC}"]`);
	if (existing) {
		return new Promise((resolve, reject) => {
			existing.addEventListener('load', resolve, { once: true });
			existing.addEventListener('error', reject, { once: true });
		});
	}
	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = APPLE_SCRIPT_SRC;
		script.async = true;
		script.onload = resolve;
		script.onerror = reject;
		document.head.appendChild(script);
	});
}

export default function AppleLoginButton({ onSuccess, onError, children = 'Continue with Apple', ...buttonProps }) {
	const [ready, setReady] = useState(false);

	useEffect(() => {
		if (!appleOAuthEnabled) return undefined;
		let active = true;
		loadAppleScript().then(() => {
			if (active) setReady(Boolean(window.AppleID?.auth));
		}).catch(() => {
			if (active) setReady(false);
		});
		return () => { active = false; };
	}, []);

	if (!appleOAuthEnabled) return null;

	const beginAppleLogin = async () => {
		if (!ready || !window.AppleID?.auth) return;
		const state = randomValue();
		const nonce = randomValue();
		try {
			window.AppleID.auth.init(buildAppleAuthorizationConfig(APPLE_CLIENT_ID, APPLE_REDIRECT_URI, state, nonce));
			const response = await window.AppleID.auth.signIn();
			if (response.authorization?.state !== state) {
				throw new Error('Apple sign-in state validation failed.');
			}
			onSuccess?.(buildAppleLoginPayload(response.authorization, APPLE_REDIRECT_URI, nonce));
		} catch (error) {
			onError?.(error);
		}
	};

	return (
		<button type="button" {...buttonProps} disabled={buttonProps.disabled || !ready} onClick={beginAppleLogin}>
			<span aria-hidden="true" className="mr-3 text-xl leading-none"></span>
			<span>{children}</span>
		</button>
	);
}
